import React, { useState, useRef, useEffect } from "react";
import FileItem from "./file-item";
import ContextMenu from "./context-menu";
import FileDropdownMenu from "./file-dropdown";
import Table from "components/table";
import "./file-browser.css";
import { formatFileSize, getListViewColumns, handleIcon } from "./filemanager-utils";

function FileBrowser({ files, isCutting, canPaste, onDoubleClickFileItem, onClickFileItem, onContextMenuClicked, isSharedFolder, isGridView, itemACtions}) {
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [contextMenuVisible, setContextMenuVisible] = useState(false);
	const [lastSelectedFile, setLastSelectedFile] = useState(null);
	const [contextMenuPosition, setContextMenuPosition] = useState({ left: 0, top: 0 });
	const [contextMenuFile, setContextMenuFile] = useState(null);
	const [hasTarget, setHasTarget] = useState(null);
	const [shouldDisabledSelection, setShouldDisabledSelection] = useState(false);
	const contextMenuRef = useRef(null);
	const fileBrowserRef = useRef(null);

	useEffect(() => {
		const appElement = document.querySelector('.file-manager__body-files');
		appElement.addEventListener("click", handleDocumentClick);
		return () => {
			appElement.removeEventListener("click", handleDocumentClick);
		};
	}, [contextMenuVisible]);


	const handleDocumentClick = (event) => {
		if(!isGridView) {
			return
		}

		event.preventDefault();

		if(event.target.closest('.file-item')) {
			return;
		}

		if (contextMenuVisible && !contextMenuRef.current.contains(event.target)) {
			setContextMenuVisible(false);
			setSelectedFiles([]);
			return;
		}
		
		if (!contextMenuVisible && !canPaste) {
		   handleClickFileItem({}, event);
		}
	};

	/**
	 * Método responsável por seleção ficheiros. Seleção simples, ou seleção múltipla através de CTR+Click ou SHIFT+Click
	 */
	const handleClickFileItem = (file, event) => {
		if(contextMenuVisible) {
			setContextMenuVisible(false);
		}

		let newSelectedFiles = file?.alias ? [file] : [];
		let isMultSelection = false;

		// handle CTRL + click 
		if (event.metaKey) {
			onMultiClickFile(file);
			return
		}

		// handle SHIF + click
		if (event.shiftKey && lastSelectedFile?.alias) {
			// Obter índice do último item selecionado e do item atual
			const lastIndex = files.indexOf(lastSelectedFile);
			const currentIndex = files.indexOf(file);

			// Verificar qual intervalo selecionar
			const [start, end] = lastIndex < currentIndex ? [lastIndex, currentIndex] : [currentIndex, lastIndex];

			// Selecionar todos os arquivos entre os dois índices
			newSelectedFiles = files.slice(start, end + 1);
			isMultSelection = true;
		}
		
		setLastSelectedFile(file);
		setSelectedFiles(newSelectedFiles);
		onClickFileItem(newSelectedFiles, isMultSelection);
	};

	/**
	 * Método responsável por abrir o context menu
	 */
	const handleContextMenuOpen = (event) => {
		event.preventDefault();

		if(!isGridView) {
			return
		}

		const { clientX, clientY } = event;
		setContextMenuPosition({ left: clientX, top: clientY });
		setHasTarget(false);

		if (event.target.dataset.alias) {
			setHasTarget(true);
			const contextMenuFile = (files ?? []).find((item) => item.alias === event.target.dataset.alias);
			setContextMenuFile(contextMenuFile);

			if (selectedFiles.length <= 1) {
				handleClickFileItem(contextMenuFile, event);
			} else if(!selectedFiles.includes(contextMenuFile)) {
				onMultiClickFile(contextMenuFile);
			}
		} else if(!canPaste) {
			handleClickFileItem([], event);
		}
		setContextMenuVisible(true);
	};

	const handleContextMenuClick = (event, option) => {
		event.stopPropagation();
		onContextMenuClicked(option, contextMenuFile);
		setContextMenuVisible(false);
	};

	const onMultiClickFile = (file) => {
		const updatedSelectedFiles = selectedFiles.includes(file)
			? selectedFiles.filter((selectedFile) => selectedFile !== file)
			: [...selectedFiles, file];

		setContextMenuVisible(false);
		setSelectedFiles(updatedSelectedFiles);
		onClickFileItem(updatedSelectedFiles, true);
	}

	const formatActions = (column, row) => {

		const isDisabled = getDisabledClass(selectedFiles, row);

		return (
			<div className="btn-group">
				<button
					key="info"
					title={__('buttons_open')}
					className="btn btn-default btn-circular"
					onClick={withPreventDefault(() => itemACtions.openFile(row))}
				>
					<em className="la la-eye" />
				</button>

				{hp.store.perms.canShare ?
					<button
						key="share"
						title={__('buttons_share')}
						className="btn btn-default btn-circular"
						onClick={withPreventDefault(() => itemACtions.shareFile(row))}
					>
						<em className="la la-share-square" />
					</button>
					: null
				}

				<FileDropdownMenu
					row={row}
					selectedFiles={[row]}
					hasTarget={row.alias ? true : false}
					isSharedFolder={isSharedFolder}
					canPaste={canPaste}
					onItemClicked={(e, option) => onContextMenuClicked(option, row)}
				/>
				<button
					key="openContextMenu"
					type="button"
					id={row.alias}
					onClick={withPreventDefault(() => onCheck([row], false))}
					disabled={isDisabled ? true : false} 
					className={`btn btn-default btn-circular ${isDisabled}`}
					data-toggle="dropdown" 
					aria-haspopup="true" 
					aria-expanded="true"
				>
					<em className="la la-ellipsis-v" />
				</button>
				
			</div>
		)
	}

	const getDisabledClass = (selectedFiles, row) => {
		return shouldDisabledSelection &&  
			((selectedFiles.length > 0 && !selectedFiles.some(f => f.alias === row.alias) || selectedFiles.length > 1))
			? 'disabled'
			: '';
	}

	const withPreventDefault = (fn) => (e) => {
		e.preventDefault()
		fn(e)
	}

	const formatSize = (column, row) => {
		return row.isFolder ? '-' : formatFileSize(Number(row.size) || 0, 2);
	}

	const onCheck = (selectedFiles, shouldDisabledSelection) => {
		setShouldDisabledSelection(shouldDisabledSelection);

		setSelectedFiles(selectedFiles);
		onClickFileItem(selectedFiles, true);
	}

	return (
		<div
			className="file-manager__body-files__table"
			ref={fileBrowserRef}
			onContextMenu={(event) => handleContextMenuOpen(event)}
		>
			<div className={`file-browser ${isGridView ? "grid-view" : "list-view"}`}>
				{files.length > 0 ? 
					( 
						isGridView ? 
							(
								files.map((file) =>  
									<FileItem
										key={`${file.alias}-file-item`}
										file={file}
										isCutting={isCutting}
										onDoubleClick={onDoubleClickFileItem.bind(this, file)}
										onClickFile={(e) => handleClickFileItem(file, e)}
										onMultiSelectionClicked={onMultiClickFile}
										selectedFiles={selectedFiles}
									/>
								)
							) : (
								<Table
									id="test"
									className="table sticky fixed alternate"
									onCheck={row => onCheck(row, true)}
									editable={false}
									onDoubleClick={row => onDoubleClickFileItem(row)}
									selectionMode={true}
									search={false}
									itemsPerPage={99}
									formatImageIcon={(row, column) => handleIcon(column, row)}
									formatActions={formatActions}
									formatSize={formatSize}
									data={files}
									columns={getListViewColumns()}
								/>
							)
					) : (
					<p className="p-2">{__('files_empty_folder')}</p>
					)
				}

				{contextMenuVisible && (
					<div  ref={contextMenuRef}>
						<ContextMenu
							hasTarget={hasTarget}
							isSharedFolder={isSharedFolder}
							contextMenuPosition={contextMenuPosition}
							canPaste={canPaste}
							selectedFiles={selectedFiles}
							onItemClicked={handleContextMenuClick}
						/>
					</div>
				)}
			</div>
		</div>
	);
}

export default FileBrowser;
